import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CalendarClock, CircleAlert, ChevronRight, ClipboardList, ExternalLink, Pencil, Users } from "lucide-react";
import NewEventDialog from "@/components/NewEventDialog";
import { dateParts } from "@/components/CompromissoCard";
import { formatParties } from "@/lib/format";

const pad = (n) => String(n).padStart(2, "0");
const localISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const minutesOf = (hhmm) => {
  if (!hhmm || !String(hhmm).includes(":")) return null;
  const [h, m] = String(hhmm).split(":").map(Number);
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
};
const isAudiencia = (u) => (u.hearing?.event_type || "audiencia") === "audiencia";

// Mesmo critério de conflito do backend (Lote 131): audiências e demais compromissos (reunião, perícia…),
// mesmo dia, menos de 60 min entre si. PRAZO não entra.
const conflitavel = (u) => (u.hearing?.event_type || "audiencia") !== "prazo";
function findConflict(upcoming) {
  const byDate = {};
  for (const u of upcoming.filter(conflitavel)) {
    const date = u.hearing?.date;
    const t = minutesOf(u.hearing?.time);
    if (!date || t == null) continue;
    (byDate[date] ||= []).push({ t, item: u });
  }
  for (const date of Object.keys(byDate).sort()) {
    const list = byDate[date].sort((a, b) => a.t - b.t);
    for (let i = 0; i < list.length - 1; i++) {
      if (list[i + 1].t - list[i].t < 60) return { date, a: list[i].item, b: list[i + 1].item };
    }
  }
  return null;
}

// "faltam 2h 32min" / "faltam 45min" / "agora"
function countdown(mins) {
  if (mins <= 0) return "agora";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `faltam ${h > 0 ? `${h}h ` : ""}${m}min`.replace(/ 0min$/, "");
}

// Reagendar: abre o MESMO formulário de edição do detalhe do evento. Busca a publicação
// completa antes (a lista de "próximos" é resumida e sem ela o formulário apagaria campos).
function RescheduleButton({ pubId, onDone }) {
  const [pub, setPub] = useState(null);
  useEffect(() => {
    let alive = true;
    http.get(`/publications/${pubId}`).then((r) => { if (alive) setPub(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, [pubId]);
  const btn = (disabled) => (
    <Button
      data-testid="conflict-reschedule-button"
      variant="outline"
      disabled={disabled}
      className="flex-1 gap-2 border-red-300 text-red-700 hover:bg-red-50"
    >
      <Pencil className="w-4 h-4" strokeWidth={1.75} /> Reagendar
    </Button>
  );
  if (!pub) return btn(true);
  return <NewEventDialog existing={pub} onCreated={onDone} trigger={btn(false)} />;
}

function CardShell({ tone, children, testid, className = "" }) {
  const tones = {
    amber: "bg-orange-50 border-orange-200",
    blue: "bg-blue-50 border-blue-200",
    red: "bg-red-50 border-red-200",
    neutral: "bg-stone-50 border-stone-200",
  };
  return (
    <div data-testid={testid} className={`rounded-xl border p-4 flex flex-col gap-3 ${tones[tone]} ${className}`}>
      {children}
    </div>
  );
}

function IconCircle({ tone, children }) {
  const tones = {
    amber: "bg-orange-200 text-orange-800",
    blue: "bg-blue-100 text-blue-700",
    red: "bg-red-100 text-red-600",
    neutral: "bg-stone-200 text-stone-500",
  };
  return <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${tones[tone]}`}>{children}</div>;
}

// Lote 69 — quadro de alertas (Painel e Agenda): Ação agora / Próxima audiência / Conflito.
// `upcoming` pode vir pronto do Painel; sem ele, busca /hearings/upcoming sozinho.
export default function AlertCards({ upcoming: upcomingProp, onChanged }) {
  const [own, setOwn] = useState([]);
  const [now, setNow] = useState(() => new Date());
  const upcoming = upcomingProp ?? own;

  useEffect(() => {
    if (upcomingProp !== undefined) return undefined;
    let alive = true;
    http.get("/hearings/upcoming").then((r) => { if (alive) setOwn(r.data || []); }).catch(() => {});
    return () => { alive = false; };
  }, [upcomingProp]);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);

  const today = localISO(now);
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const reload = () => {
    if (upcomingProp === undefined) http.get("/hearings/upcoming").then((r) => setOwn(r.data || [])).catch(() => {});
    if (onChanged) onChanged();
  };

  // Ação agora: o que é de hoje e ainda não passou (audiência/compromisso com horário futuro; prazo
  // de hoje sempre entra, com horário ou "vence hoje"). O mais próximo primeiro.
  const todays = upcoming
    .filter((u) => u.hearing?.date === today)
    .map((u) => ({ u, t: minutesOf(u.hearing?.time), isPrazo: u.hearing?.event_type === "prazo" }))
    .filter((x) => x.isPrazo || x.t == null || x.t >= nowMin)
    .sort((a, b) => (a.t ?? 24 * 60) - (b.t ?? 24 * 60));
  const action = todays[0] || null;

  // Próxima audiência: a primeira que ainda não aconteceu.
  const nextAud = upcoming
    .filter(isAudiencia)
    .find((u) => {
      const d = u.hearing?.date;
      if (!d) return false;
      if (d > today) return true;
      const t = minutesOf(u.hearing?.time);
      return d === today && (t == null || t >= nowMin);
    }) || null;

  const conflict = findConflict(upcoming);

  const actionTitle = (u) => {
    const h = u.hearing || {};
    if (h.event_type === "prazo") return `${h.deadline_type || "Prazo"} hoje`;
    if (h.event_type === "reuniao") return `${h.meeting_subject || "Compromisso"} hoje`;
    return "Audiência hoje";
  };

  const nextParts = nextAud ? dateParts(nextAud.hearing.date) : null;
  const confParts = conflict ? dateParts(conflict.date) : null;
  const confLabel = (u) => formatParties(u.hearing?.parties) || u.hearing?.case_number || "audiência";

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="alert-cards">
      {/* AÇÃO AGORA */}
      {action ? (
        <CardShell tone="amber" testid="alert-action-now">
          <Link to={`/audiencia/${action.u.pub_id}`} className="flex items-center gap-3">
            <IconCircle tone="amber"><AlertTriangle className="w-5 h-5" strokeWidth={2} /></IconCircle>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-bold tracking-[0.18em] uppercase text-orange-700">Ação agora</div>
              <div className="font-display text-lg font-bold text-stone-900 truncate">{actionTitle(action.u)}</div>
              <div className="text-xs text-stone-600 tabular-nums">
                {action.t != null ? (
                  <>
                    {action.u.hearing.time}
                    <span className="mx-1.5">·</span>
                    <span className="font-semibold text-orange-700">{countdown(action.t - nowMin)}</span>
                  </>
                ) : (
                  <span className="font-semibold text-orange-700">vence hoje</span>
                )}
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-stone-400 md:hidden" strokeWidth={1.75} />
          </Link>
          <Link to={`/audiencia/${action.u.pub_id}`} data-testid="alert-action-open">
            <Button className="w-full gap-2 bg-orange-900 hover:bg-orange-950 text-white">
              <ExternalLink className="w-4 h-4" strokeWidth={1.75} /> Abrir
            </Button>
          </Link>
        </CardShell>
      ) : (
        <CardShell tone="neutral" testid="alert-action-none" className="hidden md:flex">
          <div className="flex items-center gap-3">
            <IconCircle tone="neutral"><AlertTriangle className="w-5 h-5" strokeWidth={2} /></IconCircle>
            <div>
              <div className="text-[11px] font-bold tracking-[0.18em] uppercase text-stone-500">Ação agora</div>
              <div className="text-sm text-stone-600">Nada urgente para hoje ✓</div>
            </div>
          </div>
        </CardShell>
      )}

      {/* PRÓXIMA AUDIÊNCIA (no celular a lista de compromissos logo abaixo já mostra isso) */}
      {nextAud ? (
        <CardShell tone="blue" testid="alert-next-hearing" className="hidden md:flex">
          <div className="flex items-center gap-3">
            <IconCircle tone="blue"><CalendarClock className="w-5 h-5" strokeWidth={2} /></IconCircle>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-bold tracking-[0.18em] uppercase text-blue-700">Próxima audiência</div>
              <div className="font-display text-lg font-bold text-stone-900 tabular-nums">
                {nextParts?.dm || "—"}{nextAud.hearing.time ? ` · ${nextAud.hearing.time}` : ""}
              </div>
              <div className="text-xs text-stone-500 truncate">{confLabel(nextAud)}</div>
            </div>
          </div>
          <Link to={`/audiencia/${nextAud.pub_id}`} data-testid="alert-next-prepare">
            <Button className="w-full gap-2 bg-blue-900 hover:bg-blue-950 text-white">
              <ClipboardList className="w-4 h-4" strokeWidth={1.75} /> Preparar
            </Button>
          </Link>
        </CardShell>
      ) : (
        <CardShell tone="neutral" testid="alert-next-none" className="hidden md:flex">
          <div className="flex items-center gap-3">
            <IconCircle tone="neutral"><CalendarClock className="w-5 h-5" strokeWidth={2} /></IconCircle>
            <div>
              <div className="text-[11px] font-bold tracking-[0.18em] uppercase text-stone-500">Próxima audiência</div>
              <div className="text-sm text-stone-600">Nenhuma audiência futura</div>
            </div>
          </div>
        </CardShell>
      )}

      {/* CONFLITO */}
      {conflict ? (
        <CardShell tone="red" testid="alert-conflict">
          <div className="flex items-center gap-3">
            <IconCircle tone="red"><CircleAlert className="w-5 h-5" strokeWidth={2} /></IconCircle>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-bold tracking-[0.18em] uppercase text-red-700">Conflito detectado</div>
              <div className="font-display text-lg font-bold text-stone-900 tabular-nums">
                {confParts?.dm || "—"} · {conflict.a.hearing.time}
              </div>
              <div className="text-xs text-stone-500 truncate">
                {confLabel(conflict.a)} / {confLabel(conflict.b)} ({conflict.b.hearing.time})
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Link to={`/audiencia/${conflict.b.pub_id}#avisar-equipe`} className="flex-1 flex" data-testid="conflict-delegate-link">
              <Button variant="outline" className="flex-1 gap-2 border-stone-300">
                <Users className="w-4 h-4" strokeWidth={1.75} /> Delegar
              </Button>
            </Link>
            <RescheduleButton pubId={conflict.b.pub_id} onDone={reload} />
          </div>
        </CardShell>
      ) : (
        <CardShell tone="neutral" testid="alert-conflict-none" className="hidden md:flex">
          <div className="flex items-center gap-3">
            <IconCircle tone="neutral"><CircleAlert className="w-5 h-5" strokeWidth={2} /></IconCircle>
            <div>
              <div className="text-[11px] font-bold tracking-[0.18em] uppercase text-stone-500">Conflitos</div>
              <div className="text-sm text-stone-600">Sem conflitos de horário ✓</div>
            </div>
          </div>
        </CardShell>
      )}
    </div>
  );
}
